// Which report topic each control belongs to, so the controls can be read against the same
// frameworks as the reports (ISO 27001, NIS2, GDPR, CIS). The clause itself comes from
// reportFrameworks.js (read in complianceModel.js), so a control and the report row about the same thing always cite the same one.
// As in the reports, this is one representative clause per topic and guidance, not a legal reading:
// an auditor should sign the mapping off before it is presented as authoritative.
// One topic per genuinely distinct safeguard, not per CONTROL_CHECKS group — this is what lets
// ISO/NIS2/GDPR/M365 CIS show their own, specific clause per control instead of 17 unrelated
// device checks (or 9 unrelated mail checks) all citing the exact same paragraph. Every
// CONTROL_CHECKS id (controls.js, azure-function) is listed here explicitly; the two mail-audit
// ids keep the older 'inc' key since nothing else needed splitting there.
const TOPIC = {
  // Adgang og identitet
  'ca-admin-session': 'session-control',
  'ca-admin-phishing-resistant': 'mfa-admin',
  'ca-user-risk': 'mfa-all',
  'ca-signin-risk-mfa': 'mfa-all',
  'ca-signin-risk-block': 'mfa-all',
  'ca-register-security-info': 'device-access-control',
  'ca-intune-enrollment': 'mfa-all',
  'ca-periodic-reauth': 'session-control',
  'ca-token-protection': 'session-control',
  'ca-auth-transfer': 'legacy-auth',
  'ca-geo-block': 'location-access',
  'entra-app-registration': 'apps',
  'entra-user-consent': 'apps',
  'entra-admin-consent-workflow': 'apps',
  'entra-app-secret-addition': 'apps',
  'entra-app-secret-lifetime': 'apps',
  'entra-app-secret-generated': 'apps',
  'entra-app-certificate-lifetime': 'apps',
  'entra-tenant-creation': 'admin',
  'entra-security-groups': 'admin',
  'entra-global-admin-count': 'admin',
  'entra-guest-access': 'guests',
  'entra-guest-invites': 'guests',
  'entra-bitlocker-keys': 'device-encryption',
  'entra-device-join': 'device-enrollment',
  'entra-device-quota': 'device-enrollment',
  'entra-join-global-admin': 'device-admin-password',
  'entra-join-local-admin': 'device-admin-password',
  'entra-laps': 'device-admin-password',
  'entra-authenticator-fatigue': 'mfa-all',
  'entra-weak-methods': 'mfa-all',
  'entra-system-preferred-mfa': 'mfa-all',
  'entra-email-otp': 'mfa-all',
  'entra-authenticator-companion': 'mfa-all',
  'ca-mfa-all': 'mfa-all',
  'ca-mfa-admin': 'mfa-admin',
  'ca-mfa-guest': 'mfa-guest',
  'ca-mfa-azure': 'mfa-azure',
  'ca-block-guest-admin': 'guest-admin-block',
  'ca-legacy-auth': 'legacy-auth',
  'ca-device-code': 'legacy-auth',
  'ca-compliant-device': 'device-access-control',
  'ca-session': 'session-control',
  'ca-location': 'location-access',
  'ca-app-protection': 'mobile-app-protection',
  // Enheder (Windows/macOS/iOS/Android/enrollment)
  'win-encryption': 'device-encryption',
  'mac-encryption': 'device-encryption',
  'win-removable': 'device-encryption',
  'win-firewall': 'device-firewall',
  'win-antimalware': 'device-antimalware',
  'win-asr': 'device-antimalware',
  'win-defender-edr': 'device-edr',
  'win-password': 'device-auth',
  'win-lock': 'device-auth',
  'mac-password': 'device-auth',
  'ios-compliance': 'device-auth',
  'android-compliance': 'device-auth',
  'win-laps': 'device-admin-password',
  'win-update': 'device-patching',
  'mac-update': 'device-patching',
  'mac-firewall': 'device-firewall',
  'win-hello': 'device-auth',
  'win-local-security': 'device-auth',
  'win-backup-folders': 'device-backup',
  'ios-update': 'device-patching',
  'mac-gatekeeper': 'device-app-control',
  'enrollment': 'device-enrollment',
  // E-mail
  'mail-modern-auth': 'legacy-auth',
  'mail-smtp-auth': 'legacy-auth',
  'mail-direct-send': 'mail',
  'mail-customer-lockbox': 'mail',
  'mail-mailtips': 'mail',
  'mail-forwarding': 'mail',
  'mail-personal-accounts': 'mail',
  'mail-owa-storage': 'mail',
  'mail-outlook-addins': 'mail',
  'mail-external-tag': 'mail-phishing',
  'mail-malware-filetypes': 'mail-phishing',
  'mail-malware-notify': 'mail-phishing',
  'mail-safe-docs': 'mail-phishing',
  'mail-no-whitelist-rules': 'mail-spam',
  'mail-spam-notify': 'mail-spam',
  'mail-outbound-limits': 'mail-spam',
  'mail-ip-allow-list': 'mail-spam',
  'mail-safe-list': 'mail-spam',
  'mail-allowed-domains': 'mail-spam',
  'mail-spam-in': 'mail-spam',
  'mail-spam-out': 'mail-spam',
  'mail-antiphish': 'mail-phishing',
  'mail-safelinks': 'mail-phishing',
  'mail-safeattach': 'mail-phishing',
  'mail-external-warning': 'mail-phishing',
  'mail-dkim': 'mail-domain-auth',
  'mail-spf': 'mail-domain-auth',
  'mail-dmarc': 'mail-domain-auth',
  'mail-audit-log': 'inc',
  'mail-mailbox-audit': 'inc',
};

// A check id not listed above (shouldn't happen — every CONTROL_CHECKS id is mapped explicitly)
// falls back to a coarse bucket rather than showing no framework citation at all.
export function topicOf(control) {
  if (TOPIC[control.id]) return TOPIC[control.id];
  return control.group === 'E-mail' ? 'mail' : 'device';
}
